import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useExcJaneStreetV1PfofJobQueryServicePfofJobGet } from '@interxfi/api-client/generated/hooks';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyListStack } from '@interxfi/app-components/components/readonly-list/app-readonly-list-stack';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { DEFAULT_WRAPPING_LAYOUT_COLUMN } from '@interxfi/design-system/components/layout-container';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import { WrappingLayoutContainer } from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import { Typography } from '@interxfi/design-system/components/typography';
import type { ExcJaneStreetV1PfofJobState } from '@interxfi/types';
import { JaneStreetPfofJobEntityMenu } from '../../../../../../../../../../../features/vendor/jane-street/pfof/job/jane-street-pfof-job-entity-menu';
import { JaneStreetPfofJobEventSummary } from '../../../../../../../../../../../features/vendor/jane-street/pfof/job/jane-street-pfof-job-event';
import { getJaneStreetPfofJobStateListItems } from '../../../../../../../../../../../features/vendor/jane-street/pfof/job/jane-street-pfof-job-list-items';
import { getJaneStreetPfofJobDetailPath } from '../../../../../../../../../../../features/vendor/jane-street/pfof/job/jane-street-pfof-job-routing';
import { JaneStreetPfofJobStatusPill } from '../../../../../../../../../../../features/vendor/jane-street/pfof/job/jane-street-pfof-job-status-pill';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/vendor/_vendor/jane-street/pfof/job/_job/$jobId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `jane-street-pfof-job-${params.jobId}`,
        label: 'PFOF Job',
        to: getJaneStreetPfofJobDetailPath(params.jobId),
        suffix: <CopyButton content={params.jobId} label="Copy Job ID" tooltip={{ copy: params.jobId, copied: 'Copied Job ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Jane Street PFOF Job'),
  component: JaneStreetPfofJobDetail,
});

function getListItems(data: ExcJaneStreetV1PfofJobState | undefined) {
  const items = getJaneStreetPfofJobStateListItems(data);

  return {
    left: [
      {
        id: 'main',
        items: [items.data.file.bucket, items.data.file.key],
      },
    ] as const satisfies ReadonlyListGroup[],
    right: [items.metadata.createdAt, items.metadata.updatedAt, items.jobId] as const satisfies ReadonlyListItem[],
  } as const;
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load Jane Street PFOF job', 'view this Jane Street PFOF job');

function JaneStreetPfofJobDetail() {
  const { jobId } = Route.useParams();
  const request = { jobId } as const;
  const { data, error, isPending } = useExcJaneStreetV1PfofJobQueryServicePfofJobGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);

  const listItems = useMemo(() => getListItems(data?.pfofJob), [data?.pfofJob]);

  return (
    <PageLayout gap={DEFAULT_PAGE_LAYOUT_GAP} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section>
        <VerticalStack gap={7}>
          <PageHeader>
            <HorizontalStack gap={2} wrap="nowrap">
              <JaneStreetPfofJobStatusPill isPending={showPending} size="large" status={data?.pfofJob?.status} />
            </HorizontalStack>

            <JaneStreetPfofJobEntityMenu {...request} />
          </PageHeader>

          <VerticalStack gap={4}>
            <Typography.Level3>Events</Typography.Level3>

            <JaneStreetPfofJobEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
          </VerticalStack>
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyListStack groups={listItems.left} isPending={showPending} titleGap={4} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
